import { type FieldId, type Person, messageDeletableBy, threadDeletableBy } from '@eformulieren/model'
import * as Y from 'yjs'
import { unmarkCommentThread } from './commentRange.js'
import { DELETED, messagesOf, threadOf, threadsMap } from './sectionDocument.js'

/**
 * Een opmerking verwijderen (besluit van 2 november 2026, eisenboek 12.45, R-73).
 *
 * Twee handelingen en één regel: wie een bericht schreef, kan het verwijderen; wie een draadje begon en
 * er als enige in schreef, kan het hele draadje verwijderen. Het bericht van een ander verwijdert
 * niemand; dat draadje lost u op. De regel zelf staat in het model (`messageDeletableBy`,
 * `threadDeletableBy`), zodat de knop in de kaart en de bewaking in de pod dezelfde zijn.
 *
 * **Een markering en geen weggehaalde map** (`DELETED` in `sectionDocument.ts`). Een draadje verwijderen
 * markeert elk bericht van deze persoon; het draadje valt dan uit `threadsOf`, want een draadje zonder
 * zichtbaar bericht bestaat niet. Een antwoord van een ander dat tegelijk binnenkwam blijft zo staan, en
 * het draadje met hem.
 *
 * **Het anker gaat mee als er niets meer staat.** Is er na het verwijderen geen zichtbaar bericht meer,
 * dan haalt dezelfde transactie de mark uit de tekst (`unmarkCommentThread`). Alleen opmaak, geen letter.
 *
 * **Alleen het levende document.** Een revisie bevroor haar snapshot en haar draadjes bij de overdracht
 * (R-95, ontwerp 4.5), en een bericht dat daarna wordt verwijderd staat daar nog. Binnen de beurt staat het
 * ook nog in de geschiedenis van Yjs, want de opruiming staat uit (valkuil 1); dat hoort zo.
 *
 * Een schrijver roept dit aan op zijn eigen document; voor een meelezer doet de pod het op zijn naam
 * (`DELETE /opmerkingen/:draad`). In beide gevallen toetst de pod de regel opnieuw.
 */

export type CommentDeleteRefusal = 'draad-niet-gevonden' | 'bericht-niet-gevonden' | 'niet-van-u'

export class CommentDeleteRefused extends Error {
  constructor(readonly code: CommentDeleteRefusal) {
    super(code)
    this.name = 'CommentDeleteRefused'
  }
}

/** Wat er na een verwijdering van het draadje over is. */
export interface CommentDeleted {
  /** Het draadje is uit het levende document: er staat geen zichtbaar bericht meer in. */
  readonly draadWeg: boolean
}

/**
 * Verwijdert bericht `messageId` van `person` uit draadje `threadId`. Was het het laatste zichtbare
 * bericht, dan is het draadje weg, met zijn anker.
 */
export function deleteMessage(doc: Y.Doc, threadId: string, messageId: string, person: Person, origin?: unknown): CommentDeleted {
  const thread = threadOf(doc, threadId)
  if (thread === undefined) throw new CommentDeleteRefused('draad-niet-gevonden')
  const message = thread.berichten.find((candidate) => candidate.id === messageId)
  if (message === undefined) throw new CommentDeleteRefused('bericht-niet-gevonden')
  if (!messageDeletableBy(message, person)) throw new CommentDeleteRefused('niet-van-u')
  return mark(doc, threadId, thread.veldId, (id) => id === messageId, origin)
}

/** Verwijdert het hele draadje, als elk zichtbaar bericht van `person` is. */
export function deleteThread(doc: Y.Doc, threadId: string, person: Person, origin?: unknown): CommentDeleted {
  const thread = threadOf(doc, threadId)
  if (thread === undefined) throw new CommentDeleteRefused('draad-niet-gevonden')
  if (!threadDeletableBy(thread, person)) throw new CommentDeleteRefused('niet-van-u')
  const ids = new Set<string>(thread.berichten.map((message) => message.id))
  return mark(doc, threadId, thread.veldId, (id) => ids.has(id), origin)
}

function mark(doc: Y.Doc, threadId: string, fieldId: FieldId | undefined, chosen: (messageId: string) => boolean, origin: unknown): CommentDeleted {
  let gone = false
  doc.transact(() => {
    const map = threadsMap(doc).get(threadId)
    const messages = map === undefined ? undefined : messagesOf(map)
    for (const message of messages ?? []) {
      const id = message.get('id')
      if (typeof id === 'string' && chosen(id) && message.get(DELETED) !== true) message.set(DELETED, true)
    }
    gone = threadOf(doc, threadId) === undefined
    if (gone && fieldId !== undefined) unmarkCommentThread(doc, fieldId, threadId)
  }, origin)
  return { draadWeg: gone }
}
